// 랜딩 화면 그림(시안 3) — 누르는 곳이 없는 그림이라 버튼도 글자 상자로만 그리고, 그림마다 role="img" 설명 한 줄로 읽힌다.

import { ReactNode } from "react";
import { FileSpreadsheet, FileText, Lock, PhoneMissed, PhoneOutgoing } from "lucide-react";

import { BrandIcon } from "@buildpy/ui/brand-logo";
import Pill from "@buildpy/ui/pill";
import SectionCard from "@buildpy/ui/section-card";
import { cn } from "@/lib/utils";

// 흰 그림 카드(테두리 없이 그림자) — 시안 반경 20~28px 은 반경 토큰 최대치(rounded-lg)로 그린다.
export const MOCK_CARD = "rounded-lg bg-card text-foreground shadow-lg";

const Figure = ({ label, className, children }: { label: string; className?: string; children: ReactNode }) => (
  <div aria-label={label} className={className} role="img">
    {children}
  </div>
);

// 네모 상태 딱지 — filled 는 강조색 바탕, 아니면 연한 강조색.
const Tag = ({ children, filled = false }: { children: ReactNode; filled?: boolean }) => (
  <span className={cn("inline-flex items-center rounded-md px-2 py-0.5 text-xs font-bold", filled ? "bg-primary text-primary-foreground" : "bg-accent text-primary")}>
    {children}
  </span>
);

const Photos = ({ className }: { className?: string }) => (
  <div className={cn("grid grid-cols-3 gap-1", className)}>
    <span className="rounded-md bg-primary/15" />
    <span className="rounded-md bg-primary/25" />
    <span className="rounded-md bg-primary/15" />
  </div>
);

const Row = ({ k, v, className }: { k: ReactNode; v: ReactNode; className?: string }) => (
  <div className={cn("flex items-baseline justify-between gap-3", className)}>
    <span className="text-muted-foreground">{k}</span>
    <span className="tabular-nums">{v}</span>
  </div>
);

// 첫 화면 네 단계 카드 — 단계 순서(STEPS)와 같은 순서.
export const StepInquiry = () => (
  <Figure className="flex flex-col gap-1 text-sm" label="새 문의 카드 — 반포 32평 아파트, 홈페이지로 들어오고 디자이너 김하늘 지정">
    <div className="flex items-center justify-between">
      <Tag filled>새 문의</Tag>
      <span className="text-xs text-muted-foreground">방금</span>
    </div>
    <span className="mt-2 text-lg font-bold">반포 32평 아파트</span>
    <span className="text-muted-foreground">전체 리모델링 · 예산 5천만 원대</span>
    <Row className="mt-3" k="들어온 곳" v={<b>홈페이지</b>} />
    <Row k="원하는 디자이너" v={<b>김하늘</b>} />
  </Figure>
);

export const StepQuote = () => (
  <Figure className="flex flex-col gap-1.5 text-sm" label="고친 견적 — 가견적 55,000,000원에서 3차 48,000,000원까지, 10월 8일 고객 확인">
    <span className="font-semibold text-secondary-foreground">고친 견적</span>
    <Row className="text-muted-foreground" k="가견적" v="55,000,000" />
    <Row className="text-muted-foreground" k="1차" v="52,400,000" />
    <Row className="text-muted-foreground" k="2차" v="49,800,000" />
    <div className="flex justify-between rounded-md bg-accent px-2 py-1.5 font-bold">
      <span>3차</span>
      <span className="tabular-nums">48,000,000</span>
    </div>
    <span className="mt-1 text-xs font-bold text-primary">✓ 고객 확인 · 10월 8일</span>
  </Figure>
);

const PayBars = () => (
  <div className="grid grid-cols-10 gap-1">
    <span className="col-span-1 h-1.5 rounded-full bg-primary" />
    <span className="col-span-4 h-1.5 rounded-full bg-primary/50" />
    <span className="col-span-5 h-1.5 rounded-full bg-primary/20" />
  </div>
);

export const StepContract = () => (
  <Figure className="flex flex-col gap-1.5 text-sm" label="공사 도급 계약서 — 확인받은 견적 그대로 48,000,000원, 10월 12일 고객 서명">
    <span className="font-semibold text-secondary-foreground">공사 도급 계약서</span>
    <span className="text-2xl font-bold tabular-nums">48,000,000원</span>
    <span className="text-xs text-muted-foreground">확인받은 견적 그대로</span>
    <PayBars />
    <span className="text-xs text-muted-foreground">계약금 · 중도금 · 잔금</span>
    <span className="mt-1 text-xs font-bold text-primary">✓ 고객 서명 · 10월 12일</span>
  </Figure>
);

export const StepBuild = () => (
  <Figure className="flex flex-col gap-1.5 text-sm" label="공사 9일째 — 오늘 욕실 방수 사진과 일지, 고객에게 알림">
    <div className="flex justify-between">
      <span className="font-semibold text-secondary-foreground">공사 중</span>
      <span className="text-xs font-bold text-primary">9일째</span>
    </div>
    <Photos className="h-14" />
    <span className="mt-1 font-bold">오늘 일지 · 욕실 방수</span>
    <span className="text-xs text-muted-foreground">고객에게 알림 갔어요</span>
  </Figure>
);

const INBOX = [
  { site: "반포 32평", who: "김하늘 지정", from: "홈페이지", status: "새 문의", filled: true },
  { site: "잠실 24평", who: "담당 미정", from: "공사 안내문", status: "새 문의", filled: true },
  { site: "목동 40평", who: "박서준", from: "문의 페이지", status: "가견적 보냄", filled: false },
  { site: "분당 33평", who: "김하늘", from: "소개", status: "견적 고치는 중", filled: false },
] as const;

export const InboxMock = () => (
  <Figure className={cn(MOCK_CARD, "p-6 text-sm md:p-8")} label="문의함 — 홈페이지·공사 안내문·문의 페이지·소개로 들어온 문의 네 건">
    <div className="flex items-center justify-between">
      <span className="text-lg font-bold">문의함</span>
      <Pill className="px-3 py-1 text-sm" tone="primary">
        새 문의 2
      </Pill>
    </div>
    <div className="mt-4 grid grid-cols-7 gap-2 border-b border-border pb-2 text-xs text-muted-foreground">
      <span className="col-span-3">현장</span>
      <span className="col-span-2">들어온 곳</span>
      <span className="col-span-2">진행</span>
    </div>
    {INBOX.map((r) => (
      <div key={r.site} className="grid grid-cols-7 items-center gap-2 border-b border-border py-3 last:border-b-0">
        <span className="col-span-3 flex flex-col">
          <b>{r.site}</b>
          <span className="text-xs text-muted-foreground">{r.who}</span>
        </span>
        <span className="col-span-2">{r.from}</span>
        <span className="col-span-2">
          <Tag filled={r.filled}>{r.status}</Tag>
        </span>
      </div>
    ))}
  </Figure>
);

const QUOTE_LINES = [
  { name: "주방 상판 · 칸스톤", tag: "지난 단가", amount: "3,400,000" },
  { name: "욕실 2곳 타일 · 600각", tag: "지난 단가", amount: "2,900,000" },
  { name: "도배 · 실크 전체", tag: "템플릿", amount: "4,100,000" },
] as const;

export const QuoteRevisionMock = () => (
  <Figure className={cn(MOCK_CARD, "p-6 text-sm md:p-8")} label="3차 견적 — 템플릿에서 시작해 지난 단가가 채워지고, 2차보다 1,800,000원 줄어 48,000,000원, 10월 8일 고객 확인">
    <div className="flex gap-1.5">
      {["가견적", "1차", "2차"].map((t) => (
        <span key={t} className="rounded-full bg-muted px-3 py-1 font-semibold text-secondary-foreground">
          {t}
        </span>
      ))}
      <span className="rounded-full bg-primary px-3 py-1 font-semibold text-primary-foreground">3차</span>
    </div>
    <p className="mt-4 flex items-center gap-1.5 text-secondary-foreground">
      <FileText aria-hidden className="size-4 text-primary" />
      템플릿 <b className="text-foreground">30평대 전체 리모델링</b>에서 시작
    </p>
    <div className="mt-2">
      {QUOTE_LINES.map((l) => (
        <div key={l.name} className="flex items-center justify-between gap-2 border-b border-border py-3">
          <span className="flex flex-wrap items-center gap-1.5">
            {l.name}
            <Pill tone="primary">{l.tag}</Pill>
          </span>
          <b className="tabular-nums">{l.amount}</b>
        </div>
      ))}
    </div>
    <div className="mt-4 flex items-center justify-between gap-3 rounded-lg bg-rail px-5 py-4 text-rail-foreground">
      <span className="flex flex-col">
        <span>3차 최종 금액</span>
        <span className="text-xs">2차보다 −1,800,000</span>
      </span>
      <span className="text-3xl font-bold tabular-nums text-rail-strong">48,000,000원</span>
    </div>
    <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
      <span className="text-xs font-bold text-primary">✓ 고객이 10월 8일 확인했어요</span>
      <span className="rounded-md bg-primary px-3 py-2 text-sm font-bold text-primary-foreground">이대로 계약서 만들기 →</span>
    </div>
  </Figure>
);

export const ContractSignMock = () => (
  <Figure className={cn(MOCK_CARD, "p-6 text-sm md:p-8")} label="공사 도급 계약서 — 공사 금액 48,000,000원과 계약금·중도금·잔금, 시공사와 고객 모두 서명 완료">
    <div className="flex flex-col items-center gap-1 text-center">
      <span className="text-xl font-bold">공사 도급 계약서</span>
      <span className="text-xs text-muted-foreground">한결인테리어 · 반포 32평 아파트</span>
      <Pill className="mt-2 px-3 py-1" tone="primary">
        국토부 표준도급계약서 · 우리 회사 문구로 고침
      </Pill>
    </div>
    <div className="mt-4">
      <Row
        className="border-b border-border py-3"
        k="공사 금액"
        v={
          <>
            <b>48,000,000원</b> <span className="text-xs text-muted-foreground">확인받은 견적</span>
          </>
        }
      />
      <Row className="border-b border-border py-3" k="계약금 10%" v="4,800,000" />
      <Row className="border-b border-border py-3" k="중도금 40%" v="19,200,000" />
      <Row className="border-b border-border py-3" k="잔금 50%" v="24,000,000" />
      <Row className="py-3" k="공사 기간" v="10.20 ~ 11.20" />
    </div>
    <div className="mt-2 grid grid-cols-2 gap-2.5">
      <span className="flex flex-col rounded-md border border-dashed border-border px-3.5 py-2.5">
        <span className="text-xs text-muted-foreground">시공사</span>
        <b>서명 완료</b>
      </span>
      <span className="flex flex-col rounded-md border border-primary bg-accent px-3.5 py-2.5 text-primary">
        <span className="text-xs">고객 · 10월 12일</span>
        <b>✓ 서명 완료</b>
      </span>
    </div>
    <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
      <Lock aria-hidden className="size-3.5" />
      서명한 계약서는 고칠 수 없게 보관돼요
    </p>
  </Figure>
);

const KPIS = [
  { label: "이번 달 문의", value: "24" },
  { label: "계약", value: "10" },
  { label: "수주율", value: "42%", strong: true },
  { label: "계약이 가장 많이 나온 곳", value: "소개" },
] as const;

type BoardItem = { site: string; note: string; tone?: "primary" | "danger" } | { more: string };

const BOARD: readonly { stage: string; count: number; items: readonly BoardItem[] }[] = [
  { stage: "문의", count: 4, items: [{ site: "반포 32평", note: "홈페이지" }, { site: "잠실 24평", note: "소개" }, { more: "+2" }] },
  { stage: "견적", count: 3, items: [{ site: "분당 33평", note: "고객 확인 기다리는 중" }, { site: "목동 40평", note: "가견적" }, { more: "+1" }] },
  { stage: "착공 기다리는 중", count: 2, items: [{ site: "반포 래미안 32평", note: "착공 8일 전", tone: "primary" }, { site: "성수 28평", note: "서명 대기" }] },
  { stage: "공사 중", count: 3, items: [{ site: "목동 34평", note: "욕실 방수 · 오늘 사진 6" }, { site: "잠실 30평", note: "오늘 일지 아직", tone: "danger" }, { more: "+1" }] },
  { stage: "공사 끝", count: 1, items: [{ site: "분당 25평", note: "시공사례 글 준비됐어요" }] },
];

// compact 는 히어로 앱 화면 틀 안의 작은 판 — 폭이 좁아도 다섯 칸을 한 줄에 둔다.
export const BoardMock = ({ compact = false }: { compact?: boolean }) => (
  <Figure className={cn("flex flex-col", compact ? "gap-2" : "gap-3")} label="현장 보드 — 이번 달 문의 24건·계약 10건·수주율 42%, 문의부터 공사 끝까지 단계별 현장 카드">
    <div className={cn("grid", compact ? "grid-cols-4 gap-2" : "grid-cols-2 gap-3 md:grid-cols-4")}>
      {KPIS.map((k) =>
        "strong" in k ? (
          <div key={k.label} className={cn("flex flex-col rounded-lg bg-primary text-primary-foreground", compact ? "gap-1 p-3" : "gap-2 p-5")}>
            <span className={compact ? "text-xs" : "text-sm"}>{k.label}</span>
            <span className={cn("font-bold leading-none tabular-nums", compact ? "text-3xl" : "text-5xl")}>{k.value}</span>
          </div>
        ) : (
          <SectionCard key={k.label} className={cn("flex min-w-0 flex-col", compact ? "gap-1 p-3" : "gap-2 p-5")}>
            <span className={cn("text-muted-foreground", compact ? "truncate text-xs" : "text-sm")}>{k.label}</span>
            <span className={cn("font-bold leading-none tabular-nums", compact ? "text-3xl" : "text-5xl")}>{k.value}</span>
          </SectionCard>
        )
      )}
    </div>
    <div
      className={
        compact
          ? "grid grid-cols-5 items-start gap-2"
          : "-mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-2 md:mx-0 md:grid md:grid-cols-5 md:items-start md:overflow-visible md:px-0 md:pb-0"
      }>
      {BOARD.map((col) => (
        <div key={col.stage} className={cn("flex flex-col rounded-lg bg-muted", compact ? "min-w-0 gap-1.5 p-2" : "w-3/4 shrink-0 snap-start gap-2 p-3 md:w-auto")}>
          <div className={cn("flex justify-between gap-1 px-1 font-bold", compact ? "text-xs" : "text-sm")}>
            <span className={cn(compact && "truncate")}>{col.stage}</span>
            <span className="text-muted-foreground">{col.count}</span>
          </div>
          {col.items.map((it) =>
            "more" in it ? (
              <span key={it.more} className={cn("rounded-md bg-card font-bold", compact ? "px-2 py-1.5 text-xs" : "px-3 py-2.5 text-sm")}>
                {it.more}
              </span>
            ) : (
              <span
                key={it.site}
                className={cn("flex flex-col rounded-md bg-card", compact ? "px-2 py-1.5 text-xs" : "px-3 py-2.5 text-sm", it.tone === "primary" && "border-l-4 border-primary")}>
                <b className={cn(compact && "truncate")}>{it.site}</b>
                <span className={cn("text-xs", compact && "truncate", it.tone === "primary" ? "font-bold text-primary" : it.tone === "danger" ? "font-bold text-destructive" : "text-muted-foreground")}>
                  {it.note}
                </span>
              </span>
            )
          )}
        </div>
      ))}
    </div>
  </Figure>
);

// 사진 자리 — 사진 대신 토큰 색 네모.
const THUMB_TONES = ["bg-primary/15", "bg-warning-bg", "bg-primary/25", "bg-secondary", "bg-accent", "bg-primary/20", "bg-muted", "bg-warning-bg"] as const;

const Thumbs = ({ count, cell, className }: { count: number; cell: string; className?: string }) => (
  <div className={cn("grid gap-1", className)}>
    {THUMB_TONES.slice(0, count).map((tone, i) => (
      <span key={i} className={cn("rounded-sm", cell, tone)} />
    ))}
  </div>
);

// 히어로 오른쪽 그림 — 앱 화면 틀 속 현장 보드와 겹친 오늘 현장일지 카드(아래 #board 와 같은 판이라 읽기 프로그램에서는 숨긴다).
export const HeroBoardMock = () => (
  <div aria-hidden className="relative pb-32">
    <SectionCard className="flex w-215 flex-col overflow-hidden shadow-lg" padding="none">
      <div className="flex items-center gap-1.5 border-b border-border bg-muted px-4 py-2.5">
        <span className="size-2.5 rounded-full bg-border" />
        <span className="size-2.5 rounded-full bg-border" />
        <span className="size-2.5 rounded-full bg-border" />
      </div>
      <div className="flex">
        <div className="flex w-14 shrink-0 flex-col items-center gap-3 bg-rail py-4">
          <BrandIcon decorative tone="rail" />
          <span className="h-1.5 w-6 rounded-full bg-rail-active" />
          <span className="h-1.5 w-6 rounded-full bg-rail-active" />
          <span className="h-1.5 w-6 rounded-full bg-rail-active" />
        </div>
        <div className="min-w-0 flex-1 bg-background p-4">
          <BoardMock compact />
        </div>
      </div>
    </SectionCard>
    <SectionCard className="absolute -left-8 bottom-0 flex w-58 flex-col gap-2 p-4 text-sm shadow-lg" padding="none">
      <span className="text-xs font-bold text-secondary-foreground">성수 28평 · 오늘 현장일지</span>
      <Thumbs cell="h-12" className="grid-cols-3" count={6} />
      <span>욕실 방수 2차 끝났어요. 내일 타일 들어갑니다.</span>
      <Pill className="self-start" tone="primary">
        고객에게 알림 보냄
      </Pill>
    </SectionCard>
  </div>
);

// 흩어진 네 곳 그림 — 순서는 PAIN.items(노트북 · 통화 · 사진 · 잠금 화면).
const SCENE_HEAD = "text-xs text-muted-foreground";

// 폰 두 칸(카드 안쪽 ~141px)에선 파일명이 카드 밖으로 넘치므로 글자 단위로 줄을 바꾼다.
export const PainLaptop = () => (
  <Figure className="flex min-w-0 flex-col gap-1.5" label="디자이너 노트북 다운로드 폴더 — 견적_최종, 견적_최종_수정, 견적_진짜최종(2) 엑셀 파일 세 개">
    <span className={SCENE_HEAD}>디자이너 노트북 · 다운로드</span>
    {["견적_최종.xlsx", "견적_최종_수정.xlsx"].map((f) => (
      <span key={f} className="flex min-w-0 items-center gap-1.5 break-all font-mono text-sm text-secondary-foreground">
        <FileSpreadsheet className="size-4 shrink-0" />
        {f}
      </span>
    ))}
    <span className="flex min-w-0 items-center gap-1.5 break-all font-mono text-sm font-bold text-warning">
      <FileSpreadsheet className="size-4 shrink-0" />
      견적_진짜최종(2).xlsx
    </span>
  </Figure>
);

export const PainCall = () => (
  <Figure className="flex flex-col gap-1.5" label="지난주 화요일 14시 12분 8분 통화 — 거실 몰딩 추가 금액을 말로만 정함">
    <span className={cn(SCENE_HEAD, "flex items-center gap-1.5")}>
      <PhoneOutgoing className="size-3.5 shrink-0" />
      통화 기록 · 지난주 화요일 14:12 · 8분
    </span>
    <span className="text-sm leading-relaxed text-secondary-foreground">
      “거실 몰딩 추가하면 얼마 더…”
      <br />
      “네네, 그 정도면 돼요”
    </span>
  </Figure>
);

export const PainPhotos = () => (
  <Figure className="flex flex-col gap-1.5" label="반장님 휴대폰 사진첩 — 현장 사진 1,284장이 섞여 있음">
    <span className={SCENE_HEAD}>반장님 휴대폰 · 사진 1,284장</span>
    <Thumbs cell="h-9" className="grid-cols-4" count={8} />
  </Figure>
);

// 어두운 카드 위에 그린다 — 글자색은 레일 토큰.
export const PainLock = () => (
  <Figure className="flex flex-col gap-2" label="밤 10시 7분 잠금 화면 — 성수 고객님 부재중 전화 3통">
    <span className="text-6xl font-light leading-none tabular-nums text-rail-strong">22:07</span>
    <span className="flex items-center gap-1.5 rounded-md bg-rail-active px-2.5 py-2 text-sm text-rail-strong">
      <PhoneMissed className="size-4 shrink-0" />
      부재중 전화 3통 · 성수 고객님
    </span>
  </Figure>
);

// 계약 뒤 세 장(어두운 구간 안의 흰 그림) — 높이를 맞춘다.
const AFTER_CARD = "flex h-40 flex-col gap-2 rounded-lg bg-card p-4 text-sm text-foreground";

export const DailyMock = () => (
  <Figure className={AFTER_CARD} label="10월 29일 욕실 방수 사진 세 장 — 고객에게 알림">
    <span className="text-xs font-bold text-secondary-foreground">10월 29일 · 욕실 방수</span>
    <Photos className="h-14" />
    <span className="text-xs font-bold text-primary">고객에게 알림 갔어요</span>
  </Figure>
);

export const ChangeMock = () => (
  <Figure className={AFTER_CARD} label="추가 공사 거실 아트월 1,200,000원 — 계약 금액 4,800만에서 4,920만으로, 잔금 25,200,000원, 10월 22일 고객 합의">
    <Row k="추가 · 거실 아트월" v={<b className="text-primary">+1,200,000</b>} />
    <Row k="계약 금액" v="4,800만 → 4,920만" />
    <Row
      k="잔금"
      v={
        <>
          <s className="text-muted-foreground">24,000,000</s> <b>25,200,000</b>
        </>
      }
    />
    <span className="mt-auto text-xs font-bold text-primary">✓ 고객 합의 · 10월 22일</span>
  </Figure>
);

export const CaseMock = () => (
  <Figure className={AFTER_CARD} label="분당 25평 화이트 오크 시공사례 사진 — 홈페이지 시공사례에 올리기">
    <div className="grid h-20 grid-cols-3 grid-rows-2 gap-1">
      <span className="col-span-2 row-span-2 rounded-md bg-primary/20" />
      <span className="rounded-md bg-primary/15" />
      <span className="rounded-md bg-primary/15" />
    </div>
    <span className="text-xs font-bold">분당 25평 · 화이트 오크</span>
    <span className="text-xs font-bold text-primary">홈페이지 시공사례에 올리기</span>
  </Figure>
);
